// ─────────────────────────────────────────────────────────────────────────────
// LogEventViewController
// Detail panel for a single audit log entry.
// Shown in the details panel (UIRootViewController.detailsViewControllers).
// ─────────────────────────────────────────────────────────────────────────────

import { SocketClient } from "cbcore-ts"
import { IF, NO, UIColor, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import { RHContractingAuthorityRules } from "../ContractingAuthorityRules"
import { RHButton } from "../Custom components/RH/RHButton"
import { formatRHDateTimeWithSecondsValue } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import {
    RHButtons,
    RHColors,
    RHFont,
    RHRadius,
    RHShadow,
    RHSpacing
} from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementPartDisplay } from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHLogEventViewTokens, RHProcurementDetailsViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { ProcurementDetailsViewController } from "../ProcurementDetails/ProcurementDetailsViewController"
import { AuditLogEntry } from "../SocketClientFunctions"
import type { RHAuditEventData } from "./RHAuditEventData"

// ─────────────────────────────────────────────────────────────────────────────
// Module-level current-entry store.
// The caller sets this immediately before navigating to the route so the
// controller can read it in viewWillAppear without a server round-trip.
// ─────────────────────────────────────────────────────────────────────────────

let _currentLogEntry: AuditLogEntry | undefined

export function registerCurrentLogEntry(entry: AuditLogEntry): void {
    _currentLogEntry = entry
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function eventTypeLabel(eventType: string): string {
    const label = RHLogEventViewTokens.eventTypeLabels[eventType]
    if (label) {
        return label
    }
    return "Tundmatu sündmus"
}

function buildDetailLines(entry: AuditLogEntry): { label: string; value: string }[] {
    const data = entry.eventData as RHAuditEventData
    const lines: { label: string; value: string }[] = []
    const labels = {
        eventType: "Sündmuse tüüp",
        timestamp: "Aeg",
        user: "Kasutaja",
        company: "Ettevõte",
        target: "Objekt",
        previousDeadline: "Eelmine tähtaeg",
        newDeadline: "Uus tähtaeg",
        previousStatus: "Eelmine staatus",
        newStatus: "Uus staatus",
        partNumber: "Osa number",
        result: "Tulemus",
        openedAt: "Avatud",
        fileName: "Failinimi",
        invitedUser: "Kutsutud kasutaja",
        inviter: "Kutsuja",
        entryId: "Kirje ID"
    }
    
    lines.push({ label: labels.eventType, value: eventTypeLabel(entry.eventType) })
    lines.push({ label: labels.timestamp, value: formatRHDateTimeWithSecondsValue(entry.timestamp, "–") })
    
    const actorDisplayName = entry.actor.userDisplayName ?? entry.actor.userEmail
    if (actorDisplayName) {
        let actorLabel = labels.user
        if (entry.eventType === "company.user_invited") {
            actorLabel = labels.inviter
        }
        lines.push({ label: actorLabel, value: actorDisplayName })
    }
    if (entry.actor.companyName) {
        lines.push({ label: labels.company, value: entry.actor.companyName })
    }
    if (entry.target.name) {
        lines.push({ label: labels.target, value: entry.target.name })
    }
    
    // Event-specific fields
    switch (entry.eventType) {
        case "company.contracting_authority_types_changed":
            for (const change of [
                { label: "Eelmine määratlus", value: data.previousTypes },
                { label: "Uus määratlus", value: data.newTypes }
            ]) {
                const titles = RHContractingAuthorityRules.types
                    .filter(authorityType => Array.isArray(change.value) && change.value.includes(authorityType))
                    .map(authorityType => RHContractingAuthorityRules.definitions[authorityType].title)
                lines.push({ label: change.label, value: titles.join(", ") || "–" })
            }
            break
        case "procurement.deadline_changed":
            if (data.previousDeadline) {
                lines.push({
                    label: labels.previousDeadline,
                    value: formatRHDateTimeWithSecondsValue(data.previousDeadline, "–")
                })
            }
            if (data.newDeadline) {
                lines.push({
                    label: labels.newDeadline,
                    value: formatRHDateTimeWithSecondsValue(data.newDeadline, "–")
                })
            }
            break
        case "procurement.status_changed":
            if (data.previousStatus) {
                lines.push({ label: labels.previousStatus, value: data.previousStatus })
            }
            if (data.newStatus) {
                lines.push({ label: labels.newStatus, value: data.newStatus })
            }
            break
        case "offer.winner_selected":
            if (
                data.partNumber != null &&
                RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(data.partCount)
            ) {
                lines.push({ label: labels.partNumber, value: String(data.partNumber) })
            }
            break
        case "offer.evaluation_reopened":
            if (data.partNumber != null && RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(data.partCount)) {
                lines.push({ label: labels.partNumber, value: String(data.partNumber) })
            }
            lines.push({ label: "Varasem otsus", value: "Edukaks tunnistamise otsus ei kehti enam" })
            lines.push({ label: "Otsuse muutmise põhjus", value: String(data.reason ?? "") })
            let withdrawalDescription = "Ei"
            if (data.winningOfferWasWithdrawn === YES) {
                withdrawalDescription = "Jah"
            }
            lines.push({ label: "Senine edukas pakkuja loobus", value: withdrawalDescription })
            break
        case "offer.notification_sent": {
            let result = "Pakkumust ei tunnistatud edukaks"
            if (data.wasExcludedFromNextRound === YES) {
                result = "Pakkujat ei kutsutud järgmisse vooru"
            }
            else if (data.isWinner) {
                result = "Pakkumus tunnistati edukaks"
            }
            else if (data.partHasWinner === NO) {
                result = "Ühtegi pakkumust ei tunnistatud edukaks"
            }
            lines.push({ label: labels.result, value: result })
            break
        }
        case "offer.opened":
            if (data.openedAt) {
                lines.push({
                    label: labels.openedAt,
                    value: formatRHDateTimeWithSecondsValue(data.openedAt, "–")
                })
            }
            break
        case "file.uploaded":
        case "file.deleted":
            if (data.fileName) {
                lines.push({ label: labels.fileName, value: data.fileName })
            }
            break
        case "company.user_invited":
            if (data.userDisplayName) {
                lines.push({ label: labels.invitedUser, value: data.userDisplayName })
            }
            break
        default:
            break
    }
    
    lines.push({ label: labels.entryId, value: entry._id })
    
    return lines
}

// ─────────────────────────────────────────────────────────────────────────────
// Row view — one label/value pair
// ─────────────────────────────────────────────────────────────────────────────

class DetailRowView extends UIView {
    
    readonly labelView = new UITextView().configuredWithObject({
        backgroundColor: UIColor.transparentColor,
        isSingleLine: YES
    }).addedAsSubviewToView(this)
    
    readonly valueView = new UITextView().configuredWithObject({
        backgroundColor: UIColor.transparentColor,
        isSingleLine: NO,
        style: { overflowWrap: "anywhere" }
    }).addedAsSubviewToView(this)
    
    configure(label: string, value: string): void {
        this.labelView.configureWithObject({
            text: label,
            textColor: RHColors.textPrimary,
            style: {
                fontSize: "10pt",
                fontWeight: RHProcurementDetailsViewTokens.detailHeaderFontWeight
            }
        })
        this.valueView.configureWithObject({
            text: RHTextSanitizer.escapedHTML(value),
            textColor: RHColors.textSecondary,
            style: {
                fontSize: RHFont.sizeMedium,
                fontWeight: RHProcurementDetailsViewTokens.detailValueFontWeight
            }
        })
        this.valueView.setUseFastMeasurement(NO)
    }
    
    override layoutSubviews(): void {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(0)
        
        bounds
            .rectangleWithHeight(RHSpacing.medium)
            .assignedAsFrameOfView(this.labelView)
            .rectangleForNextRow(0, this.valueView)
            .assignedAsFrameOfView(this.valueView)
    }
    
    override intrinsicContentHeight(constrainingWidth = 0): number {
        return Math.max(
            RHLogEventViewTokens.rowHeight,
            RHSpacing.medium + this.valueView.intrinsicContentHeight(constrainingWidth)
        )
    }

}

// ─────────────────────────────────────────────────────────────────────────────
// LogEventViewController
// ─────────────────────────────────────────────────────────────────────────────

export class LogEventViewController extends UIViewController {
    
    static override readonly routeComponentName = "logEvent"
    static override readonly ParameterIdentifierName = {
        id: "id"
    }
    
    // ── Subviews ──────────────────────────────────────────────────────────────
    
    readonly contentView = new UIView(this.view.elementID + "ContentView").configuredWithObject({
        backgroundColor: UIColor.whiteColor,
        style: {
            borderRadius: `${RHRadius.large}px`,
            boxShadow: RHShadow.strong
        }
    }).addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        // UITextView.type.header2
        UITextView.type.header4
    ).configuredWithObject({
        text: "Sündmuse detailid",
        textColor: RHColors.textPrimary,
        backgroundColor: UIColor.transparentColor
    }).addedAsSubviewToView(this.contentView)

    readonly closeButton = new RHButton().configuredWithObject({
        hoverText: "Sulge",
        titleLabel: {
            text: RHButtons.dialogCloseTitle,
            style: {
                fontSize: RHButtons.dialogCloseFontSize,
                lineHeight: RHButtons.dialogCloseLineHeight
            }
        },
        variant: RHButtons.dialogCloseVariant,
        contentPadding: RHButtons.dialogCloseContentPadding,
        style: {
            padding: RHButtons.dialogClosePadding,
            borderRadius: RHButtons.dialogCloseBorderRadius
        }
    }).addedAsSubviewToView(this.contentView)
    
    readonly jumpToProcurementButton = new RHButton().configuredWithObject({
        titleLabel: {
            text: "Vaata hanget",
            style: { fontSize: RHButtons.compactPrimaryFontSize }
        },
        contentPadding: RHButtons.compactPrimaryContentPadding
    }).addedAsSubviewToView(this.contentView)
    
    _detailRows: DetailRowView[] = []
    
    // ── State ─────────────────────────────────────────────────────────────────
    
    _currentEntry: AuditLogEntry | undefined
    
    // ── Constructor ───────────────────────────────────────────────────────────
    
    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({
            backgroundColor: UIColor.transparentColor,
            intrinsicContentWidth: constrainingHeight => RHLogEventViewTokens.dialogMaxWidth
        })
        this.jumpToProcurementButton.hidden = YES
        
        // noinspection JSConstantReassignment
        this.jumpToProcurementButton.controlEventTargetAccumulator.PointerUpInside = () => {
            this._jumpToProcurement()
        }
        // noinspection JSConstantReassignment
        this.closeButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._close()
        }
    }
    
    // ── Private ───────────────────────────────────────────────────────────────
    
    _procurementIdForEntry(entry: AuditLogEntry): string {
        return (entry.target.type === "procurement" ? entry.target.id : "") ||
            (entry.eventData?.procurementId as string) || ""
    }
    
    _close(): void {
        UIRoute.currentRoute
            .routeByRemovingComponentNamed(LogEventViewController.routeComponentName)
            .apply()
    }

    _jumpToProcurement(): void {
        if (!this._currentEntry) {
            return
        }
        const procurementId = this._procurementIdForEntry(this._currentEntry)
        if (!procurementId) {
            return
        }
        UIRoute.currentRoute
            .routeByRemovingComponentNamed(LogEventViewController.routeComponentName)
            .routeWithViewControllerComponent(
                ProcurementDetailsViewController,
                { [ProcurementDetailsViewController.ParameterIdentifierName.id]: procurementId }
            )
            .apply()
    }
    
    _rebuildRows(entry: AuditLogEntry): void {
        for (const row of this._detailRows) {
            row.removeFromSuperview()
        }
        this._detailRows = []
        
        const lines = buildDetailLines(entry)
        for (const line of lines) {
            const row = new DetailRowView().addedAsSubviewToView(this.contentView)
            row.configure(line.label, line.value)
            this._detailRows.push(row)
        }
        
        const procurementId = this._procurementIdForEntry(entry)
        this.jumpToProcurementButton.hidden = !procurementId
        
        this.view.setNeedsLayout()
    }
    
    // ── Lifecycle ─────────────────────────────────────────────────────────────
    
    override async viewWillAppear(): Promise<void> {
        await super.viewWillAppear()
        
        // Fast path: the caller set the entry in the module-level store (normal navigation)
        if (_currentLogEntry) {
            this._currentEntry = _currentLogEntry
            _currentLogEntry = undefined
            this._rebuildRows(this._currentEntry)
            return
        }
        
        // Slow path: page was refreshed — read the entry ID from the route and fetch from server
        const entryId = UIRoute.currentRoute
            .componentWithName(LogEventViewController.routeComponentName)
            ?.parameters[LogEventViewController.ParameterIdentifierName.id]
        
        if (!entryId) {
            return
        }
        
        try {
            const response = await SocketClient.GetAuditLogEntry({
                id: entryId,
                companyId: RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
            })
            this._currentEntry = response.result.log
            this._rebuildRows(this._currentEntry)
        }
        catch (error) {
            console.error("LogEventViewController: failed to fetch entry", error)
        }
    }
    
    override async handleRoute(route: UIRoute): Promise<void> {
        super.handleRoute(route)
    }
    
    // ── Layout ────────────────────────────────────────────────────────────────
    
    _footerButtonWidth(button: RHButton): number {
        return Math.min(
            (RHSpacing.xxLarge * 4),
            Math.max(
                (RHSpacing.xxLarge * 3),
                button.titleLabel.intrinsicContentWidth(RHButtons.compactPrimaryHeight) +
                RHButtons.compactPrimaryContentPadding * 2 +
                RHSpacing.medium
            )
        )
    }

    _contentHeightForWidth(contentWidth: number): number {
        const rowCount = this._detailRows.length
        const rowsHeight =
            this._detailRows.reduce((height, row) => height + row.intrinsicContentHeight(contentWidth), 0) +
            Math.max(0, rowCount - 1) * RHSpacing.small
        const footerHeight = IF(this.jumpToProcurementButton.hidden)(() => 0)
            .ELSE(() => RHSpacing.large + RHButtons.compactPrimaryHeight)

        return RHSpacing.large * 2 +
            RHSpacing.xLarge +
            RHSpacing.medium +
            rowsHeight +
            footerHeight +
            RHSpacing.small
    }

    override layoutViewSubviews(): void {
        super.layoutViewSubviews()
        
        const superBounds = this.view.contentBoundsWithInset(0)
        const dialogWidth = Math.min(
            RHLogEventViewTokens.dialogMaxWidth,
            superBounds.width * 0.92
        )
        
        this.contentView.frame = superBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(this._contentHeightForWidth(dialogWidth - RHSpacing.large * 2), 0.5)
        
        const bounds = this.contentView.contentBoundsWithInset(RHSpacing.large)
        const closeButtonSize = RHButtons.dialogCloseSize
        const headerRowFrame = bounds.rectangleWithHeight(RHSpacing.xLarge)
        const titleFrame = headerRowFrame.rectangleWithWidth(
            headerRowFrame.width - closeButtonSize - RHSpacing.small
        )

        titleFrame
            .assignedAsFrameOfView(this.titleLabel)

        titleFrame
            .rectangleForNextColumn(RHSpacing.small, closeButtonSize)
            .rectangleWithHeight(closeButtonSize, 0.5)
            .assignedAsFrameOfView(this.closeButton)

        let currentFrame = headerRowFrame

        for (let index = 0; index < this._detailRows.length; index++) {
            const row = this._detailRows[index]
            currentFrame = currentFrame.rectangleForNextRow(
                IF(index === 0)(() => RHSpacing.medium).ELSE(() => RHSpacing.small),
                row
            )
            row.frame = currentFrame
        }

        if (!this.jumpToProcurementButton.hidden) {
            currentFrame
                .rectangleForNextRow(
                    RHSpacing.large,
                    RHButtons.compactPrimaryHeight
                )
                .rectangleWithWidth(this._footerButtonWidth(this.jumpToProcurementButton), 0.5)
                .assignedAsFrameOfView(this.jumpToProcurementButton)
        }
    }
    
}
